import { z } from "zod";

import {
  BRAND_ONBOARDING_CONFIRMED_STRATEGY_KEY,
  BRAND_VISUAL_UNIVERSE_MANUAL_STRATEGY_KEY,
  brandVisualUniverseSchema,
  brandVoiceCommunicationStyleSchema,
  brandVoiceSocialExpressionSchema,
  coreBrandIdentitySchema,
  projectBrandVisualUniverseToLook,
  positioningMarketContextSchema,
  targetAudienceSchema,
  visualIdentitySnapshotSchema,
  type BrandVisualUniverse,
  type BrandVoiceCommunicationStyle,
  type CoreBrandIdentity,
  type PositioningMarketContext,
  type TargetAudience,
  type VisualIdentitySnapshot,
} from "./brand-profile.ts";
import { buildBrandProfilePromptContext } from "./brand-profile-prompt-context.ts";

const USER_CONFIRMED_FACT_STRATEGY_KEYS = new Set([
  "user_edit",
  BRAND_ONBOARDING_CONFIRMED_STRATEGY_KEY,
  BRAND_VISUAL_UNIVERSE_MANUAL_STRATEGY_KEY,
]);

export interface BrandFactClaimCandidate {
  id?: string;
  fieldPath: string;
  valueJson: unknown;
  strategyKey?: string | null;
  confidence?: number | string | null;
  evidenceQuote?: string | null;
  rationale?: string | null;
  sourceUrl?: string | null;
  traceId?: string | null;
  createdAt?: Date | string | null;
}

export const resolvedBrandContextV1Schema = z.object({
  version: z.literal(1),
  canonicalFields: z.record(z.string().min(1), z.unknown().nullable()),
});
export type ResolvedBrandContextV1 = z.infer<typeof resolvedBrandContextV1Schema>;

export const resolvedBrandFactProvenanceV1Schema = z.object({
  canonicalFieldPath: z.string().min(1),
  sourceFactId: z.string().min(1).nullable(),
  sourceFieldPath: z.string().min(1),
  strategyKey: z.string().min(1).nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  evidenceQuote: z.string().nullable(),
  rationale: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type ResolvedBrandFactProvenanceV1 = z.infer<typeof resolvedBrandFactProvenanceV1Schema>;

export const resolvedBrandContextProvenanceV1Schema = z.object({
  version: z.literal(1),
  facts: z.array(resolvedBrandFactProvenanceV1Schema),
});
export type ResolvedBrandContextProvenanceV1 = z.infer<
  typeof resolvedBrandContextProvenanceV1Schema
>;

type NullableFields<T> = { [K in keyof T]-?: T[K] | null };

export interface ResolvedBrandContextV2 {
  version: 2;
  coreIdentity: NullableFields<CoreBrandIdentity>;
  brandVoiceCommunicationStyle: NullableFields<
    Omit<BrandVoiceCommunicationStyle, "socialExpression">
  >;
  positioningMarketContext: NullableFields<PositioningMarketContext>;
  targetAudience: NullableFields<TargetAudience>;
  visualIdentitySnapshot: NullableFields<VisualIdentitySnapshot>;
  brandVisualUniverse: NullableFields<BrandVisualUniverse>;
}

export const BRAND_KIT_FIELD_OWNERS = {
  palette: "brand-style",
  audienceSegments: "content-preferences",
  painPoints: "brand-profile",
  contentAngles: "brand-voice",
  voiceBasics: "brand-voice",
  socialMechanics: "brand-voice",
  competitors: "brand-profile",
} as const;

export const BRAND_VOICE_BASIC_FIELD_PATHS = [
  "brandVoiceCommunicationStyle.tone",
  "brandVoiceCommunicationStyle.voice",
  "brandVoiceCommunicationStyle.vocabularyStyle",
  "brandVoiceCommunicationStyle.sentenceComplexity",
  "brandVoiceCommunicationStyle.useOfEmoji",
  "brandVoiceCommunicationStyle.culturalReferences",
] as const;

const IMAGE_BRAND_VISUAL_UNIVERSE_SOURCE_FIELD_PATHS = [
  "brandVisualUniverse.photographyArchetypes",
  "brandVisualUniverse.materials",
  "brandVisualUniverse.motifs",
  "brandVisualUniverse.colorRoles",
  "brandVisualUniverse.compositionRules",
  "brandVisualUniverse.antiPatterns",
] as const;

const BRAND_VISUAL_UNIVERSE_FIELD_PATH_PREFIX = "brandVisualUniverse.";
const BRAND_VISUAL_LOOK_FIELD_PATH = "brandVisualLook";

export const RESOLVED_BRAND_CONTEXT_TASK_FIELD_PATHS = {
  campaign: [
    "coreIdentity.brandName",
    "coreIdentity.mission",
    "coreIdentity.vision",
    "coreIdentity.brandPromise",
    "coreIdentity.uniqueSellingProposition",
    "coreIdentity.keyDifferentiators",
    "positioningMarketContext.marketCategory",
    "positioningMarketContext.marketSubcategoryNiche",
    "positioningMarketContext.currentCommercialMoment",
    "positioningMarketContext.marketMaturityLevel",
    "positioningMarketContext.perceivedPositioning",
    "positioningMarketContext.competitiveAdvantages",
    "positioningMarketContext.competitiveDisadvantages",
    "positioningMarketContext.differentiationStrategy",
    "targetAudience.audienceProfileSummary",
    "targetAudience.primaryAudienceSegments",
    "targetAudience.secondaryAudienceSegments",
    "targetAudience.demographics",
    "targetAudience.psychographics",
    "targetAudience.behavioralTraits",
    "targetAudience.painPoints",
    "targetAudience.desiresAspirations",
    "targetAudience.buyingTriggers",
    "targetAudience.awarenessStage",
    "targetAudience.decisionMakingDrivers",
    "targetAudience.geographicMarkets",
    "targetAudience.resonanceArchetypeScores",
    "targetAudience.humanLenses",
    "targetAudience.domainTerritories",
    "targetAudience.postTypeResonance",
    ...BRAND_VOICE_BASIC_FIELD_PATHS,
    "brandVoiceCommunicationStyle.brandPersonalityTraits",
    "brandVoiceCommunicationStyle.contentDnaPrimaryStyle",
    "brandVoiceCommunicationStyle.contentDnaSecondaryStyle",
    "brandVoiceCommunicationStyle.contentDnaConfidence",
    "brandVoiceCommunicationStyle.contentDnaHumorLevel",
    "brandVoiceCommunicationStyle.contentDnaOpinionatedness",
    "brandVoiceCommunicationStyle.contentDnaAspirationLevel",
    "brandVoiceCommunicationStyle.contentDnaEducationLevel",
    "brandVoiceCommunicationStyle.contentDnaCommunityLevel",
    "brandVoiceCommunicationStyle.socialMechanics",
    "brandVoiceCommunicationStyle.socialAvoidedMechanics",
    "brandVoiceCommunicationStyle.socialTruths",
    "brandVoiceCommunicationStyle.audienceTensions",
    "brandVoiceCommunicationStyle.shareabilityTriggers",
    "brandVoiceCommunicationStyle.contentAngles",
    "brandVoiceCommunicationStyle.contentAntiPatterns",
  ],
  copy: [
    "coreIdentity.brandName",
    "coreIdentity.brandPromise",
    "coreIdentity.uniqueSellingProposition",
    "coreIdentity.keyDifferentiators",
    "positioningMarketContext.marketCategory",
    "positioningMarketContext.marketSubcategoryNiche",
    "positioningMarketContext.perceivedPositioning",
    "targetAudience.audienceProfileSummary",
    "targetAudience.primaryAudienceSegments",
    "targetAudience.secondaryAudienceSegments",
    "targetAudience.psychographics",
    "targetAudience.behavioralTraits",
    "targetAudience.painPoints",
    "targetAudience.desiresAspirations",
    "targetAudience.buyingTriggers",
    "targetAudience.decisionMakingDrivers",
    "targetAudience.humanLenses",
    "targetAudience.domainTerritories",
    ...BRAND_VOICE_BASIC_FIELD_PATHS,
    "brandVoiceCommunicationStyle.brandPersonalityTraits",
    "brandVoiceCommunicationStyle.contentDnaPrimaryStyle",
    "brandVoiceCommunicationStyle.contentDnaSecondaryStyle",
    "brandVoiceCommunicationStyle.contentDnaHumorLevel",
    "brandVoiceCommunicationStyle.contentDnaOpinionatedness",
    "brandVoiceCommunicationStyle.contentDnaAspirationLevel",
    "brandVoiceCommunicationStyle.contentDnaEducationLevel",
    "brandVoiceCommunicationStyle.contentDnaCommunityLevel",
    "brandVoiceCommunicationStyle.socialMechanics",
    "brandVoiceCommunicationStyle.socialAvoidedMechanics",
    "brandVoiceCommunicationStyle.socialTruths",
    "brandVoiceCommunicationStyle.audienceTensions",
    "brandVoiceCommunicationStyle.shareabilityTriggers",
    "brandVoiceCommunicationStyle.contentAngles",
    "brandVoiceCommunicationStyle.contentAntiPatterns",
    "brandVoiceCommunicationStyle.restrictedTopics",
    "brandVoiceCommunicationStyle.wordsToAvoid",
    "brandVoiceCommunicationStyle.wordsToEmphasize",
  ],
  image: [
    "coreIdentity.brandName",
    "coreIdentity.taglineSlogan",
    "coreIdentity.uniqueSellingProposition",
    "targetAudience.audienceProfileSummary",
    "targetAudience.primaryAudienceSegments",
    "targetAudience.secondaryAudienceSegments",
    "targetAudience.psychographics",
    "targetAudience.desiresAspirations",
    "brandVoiceCommunicationStyle.tone",
    "brandVoiceCommunicationStyle.voice",
    "brandVoiceCommunicationStyle.contentAngles",
    "brandVoiceCommunicationStyle.audienceTensions",
    ...Object.keys(visualIdentitySnapshotSchema.shape).map(
      (field) => `visualIdentitySnapshot.${field}`,
    ),
    ...IMAGE_BRAND_VISUAL_UNIVERSE_SOURCE_FIELD_PATHS,
    BRAND_VISUAL_LOOK_FIELD_PATH,
  ],
} as const satisfies Record<string, readonly string[]>;

export type ResolvedBrandContextTask = keyof typeof RESOLVED_BRAND_CONTEXT_TASK_FIELD_PATHS;

export interface BrandContextResolutionIssue {
  code: "empty_value" | "legacy_field" | "normalized_value";
  fieldPath: string;
  sourceFieldPath: string;
  before: unknown;
  after: unknown | null;
}

const nullableSectionSchema = (
  shape: Record<string, z.ZodType>,
  excludedKeys: ReadonlySet<string> = new Set(),
) =>
  z.object(
    Object.fromEntries(
      Object.entries(shape).flatMap(([key, schema]) =>
        excludedKeys.has(key)
          ? []
          : [
              [
                key,
                z
                  .preprocess((value) => (value === undefined ? null : value), schema.nullable())
                  .default(null),
              ],
            ],
      ),
    ),
  );

export const resolvedBrandContextV2Schema: z.ZodType<ResolvedBrandContextV2> = z.object({
  version: z.literal(2),
  coreIdentity: nullableSectionSchema(coreBrandIdentitySchema.shape),
  brandVoiceCommunicationStyle: nullableSectionSchema(
    brandVoiceCommunicationStyleSchema.shape,
    new Set(["socialExpression"]),
  ),
  positioningMarketContext: nullableSectionSchema(positioningMarketContextSchema.shape),
  targetAudience: nullableSectionSchema(targetAudienceSchema.shape),
  visualIdentitySnapshot: nullableSectionSchema(visualIdentitySnapshotSchema.shape),
  brandVisualUniverse: nullableSectionSchema(brandVisualUniverseSchema.shape),
}) as unknown as z.ZodType<ResolvedBrandContextV2>;

export const resolvedBrandClaimProvenanceV2Schema = z.object({
  sourceFieldPath: z.string().min(1),
  strategyKey: z.string().min(1).nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  evidenceQuote: z.string().nullable(),
  rationale: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  traceId: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type ResolvedBrandClaimProvenanceV2 = z.infer<typeof resolvedBrandClaimProvenanceV2Schema>;

export const resolvedBrandContextProvenanceV2Schema = z.object({
  version: z.literal(2),
  selectedClaimIds: z.record(z.string().min(1), z.string().min(1)),
  claimsById: z.record(z.string().min(1), resolvedBrandClaimProvenanceV2Schema),
});
export type ResolvedBrandContextProvenanceV2 = z.infer<
  typeof resolvedBrandContextProvenanceV2Schema
>;

export type ResolvedBrandContext = ResolvedBrandContextV1 | ResolvedBrandContextV2;
export type ResolvedBrandContextProvenance =
  | ResolvedBrandContextProvenanceV1
  | ResolvedBrandContextProvenanceV2;

export interface ResolvedBrandContextSnapshotV1 {
  id: string;
  brandId: string;
  schemaVersion: 1;
  context: ResolvedBrandContextV1;
  provenance: ResolvedBrandContextProvenanceV1;
  createdAt: Date;
}

export interface ResolvedBrandContextSnapshotV2 {
  id: string;
  brandId: string;
  schemaVersion: 2;
  context: ResolvedBrandContextV2;
  provenance: ResolvedBrandContextProvenanceV2;
  createdAt: Date;
}

export type ResolvedBrandContextSnapshot =
  | ResolvedBrandContextSnapshotV1
  | ResolvedBrandContextSnapshotV2;

export const normalizeBrandFactValue = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
};

const factSourcePriority = (fact: BrandFactClaimCandidate): number =>
  fact.strategyKey && USER_CONFIRMED_FACT_STRATEGY_KEYS.has(fact.strategyKey) ? 1 : 0;

const factConfidence = (fact: BrandFactClaimCandidate): number => {
  const parsed =
    typeof fact.confidence === "number"
      ? fact.confidence
      : Number.parseFloat(fact.confidence ?? "");
  return Number.isFinite(parsed) ? parsed : -1;
};

const factCreatedAt = (fact: BrandFactClaimCandidate): number => {
  if (!fact.createdAt) return 0;
  const timestamp =
    fact.createdAt instanceof Date ? fact.createdAt.getTime() : Date.parse(fact.createdAt);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const compareBrandFactClaims = (
  left: BrandFactClaimCandidate,
  right: BrandFactClaimCandidate,
): number => {
  const sourceDifference = factSourcePriority(right) - factSourcePriority(left);
  if (sourceDifference !== 0) return sourceDifference;

  const confidenceDifference = factConfidence(right) - factConfidence(left);
  if (confidenceDifference !== 0) return confidenceDifference;

  const createdAtDifference = factCreatedAt(right) - factCreatedAt(left);
  if (createdAtDifference !== 0) return createdAtDifference;

  return (left.id ?? "\uffff").localeCompare(right.id ?? "\uffff");
};

export const selectCanonicalBrandFactClaims = (
  facts: readonly BrandFactClaimCandidate[],
): Map<string, BrandFactClaimCandidate> => {
  const selected = new Map<string, BrandFactClaimCandidate>();
  for (const fact of facts) {
    const normalized = { ...fact, valueJson: normalizeBrandFactValue(fact.valueJson) };
    const current = selected.get(fact.fieldPath);
    if (!current || compareBrandFactClaims(normalized, current) < 0) {
      selected.set(fact.fieldPath, normalized);
    }
  }
  return selected;
};

const LEGACY_SOCIAL_EXPRESSION_PATH = "brandVoiceCommunicationStyle.socialExpression";
const LEGACY_SOCIAL_EXPRESSION_CANONICAL_FIELDS = {
  primaryStyle: "brandVoiceCommunicationStyle.contentDnaPrimaryStyle",
  secondaryStyle: "brandVoiceCommunicationStyle.contentDnaSecondaryStyle",
  confidence: "brandVoiceCommunicationStyle.contentDnaConfidence",
  opinionatedness: "brandVoiceCommunicationStyle.contentDnaOpinionatedness",
  aspirationLevel: "brandVoiceCommunicationStyle.contentDnaAspirationLevel",
  educationLevel: "brandVoiceCommunicationStyle.contentDnaEducationLevel",
  communityLevel: "brandVoiceCommunicationStyle.contentDnaCommunityLevel",
  preferredMechanics: "brandVoiceCommunicationStyle.socialMechanics",
  avoidedMechanics: "brandVoiceCommunicationStyle.socialAvoidedMechanics",
  rationale: "brandVoiceCommunicationStyle.contentDnaRationale",
} as const;

const CANONICAL_SECTION_SCHEMAS = {
  coreIdentity: coreBrandIdentitySchema,
  brandVoiceCommunicationStyle: brandVoiceCommunicationStyleSchema,
  positioningMarketContext: positioningMarketContextSchema,
  targetAudience: targetAudienceSchema,
  visualIdentitySnapshot: visualIdentitySnapshotSchema,
  brandVisualUniverse: brandVisualUniverseSchema,
} as const;

export const RESOLVED_BRAND_CONTEXT_V2_FIELD_PATHS = Object.entries(
  CANONICAL_SECTION_SCHEMAS,
).flatMap(([section, schema]) =>
  Object.keys(schema.shape).flatMap((field) =>
    section === "brandVoiceCommunicationStyle" && field === "socialExpression"
      ? []
      : [`${section}.${field}`],
  ),
);
const RESOLVED_BRAND_CONTEXT_V2_FIELD_PATH_SET = new Set(RESOLVED_BRAND_CONTEXT_V2_FIELD_PATHS);

const parseCanonicalClaimValue = (fieldPath: string, value: unknown): unknown | undefined => {
  if (value === null || value === undefined) return undefined;
  const separator = fieldPath.indexOf(".");
  if (separator < 1) return undefined;
  const section = fieldPath.slice(0, separator) as keyof typeof CANONICAL_SECTION_SCHEMAS;
  const field = fieldPath.slice(separator + 1);
  const shape = CANONICAL_SECTION_SCHEMAS[section]?.shape as Record<string, z.ZodType> | undefined;
  const schema = shape?.[field];
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "") return undefined;
  const parsed = schema?.safeParse(normalizedValue);
  return parsed?.success ? parsed.data : undefined;
};

type CanonicalClaimCandidate = BrandFactClaimCandidate & { sourceFieldPath: string };

const expandCanonicalCandidatesUnfiltered = (
  facts: readonly BrandFactClaimCandidate[],
): CanonicalClaimCandidate[] =>
  facts.flatMap((fact) => {
    const normalizedValue = normalizeBrandFactValue(fact.valueJson);
    if (fact.fieldPath !== LEGACY_SOCIAL_EXPRESSION_PATH) {
      if (!RESOLVED_BRAND_CONTEXT_V2_FIELD_PATH_SET.has(fact.fieldPath)) return [];
      const valueJson = parseCanonicalClaimValue(fact.fieldPath, normalizedValue);
      if (valueJson === undefined) {
        const isExplicitUserClear =
          Boolean(fact.strategyKey && USER_CONFIRMED_FACT_STRATEGY_KEYS.has(fact.strategyKey)) &&
          ((typeof normalizedValue === "string" && normalizedValue.trim() === "") ||
            (Array.isArray(normalizedValue) && normalizedValue.length === 0));
        return isExplicitUserClear
          ? [{ ...fact, valueJson: null, sourceFieldPath: fact.fieldPath }]
          : [];
      }
      const canonical = [{ ...fact, valueJson, sourceFieldPath: fact.fieldPath }];
      if (fact.fieldPath !== "targetAudience.humanLenses") return canonical;
      const humanLenses = targetAudienceSchema.shape.humanLenses.safeParse(valueJson);
      if (!humanLenses.success || !humanLenses.data) return canonical;
      const hookTensions = humanLenses.data.map((lens) => lens.audienceTension);
      return [
        ...canonical,
        {
          ...fact,
          fieldPath: "brandVoiceCommunicationStyle.audienceTensions",
          valueJson: hookTensions,
          sourceFieldPath: fact.fieldPath,
        },
      ];
    }

    const legacy = brandVoiceSocialExpressionSchema.safeParse(normalizedValue);
    const legacyValue = legacy.success
      ? legacy.data
      : normalizedValue && typeof normalizedValue === "object" && !Array.isArray(normalizedValue)
        ? (normalizedValue as Record<string, unknown>)
        : null;
    if (!legacyValue) return [];
    return Object.entries(LEGACY_SOCIAL_EXPRESSION_CANONICAL_FIELDS).flatMap(
      ([legacyKey, canonicalPath]) => {
        const valueJson = parseCanonicalClaimValue(
          canonicalPath,
          Reflect.get(legacyValue, legacyKey),
        );
        return valueJson === undefined
          ? []
          : [{ ...fact, fieldPath: canonicalPath, sourceFieldPath: fact.fieldPath, valueJson }];
      },
    );
  });

const expandCanonicalCandidates = (
  facts: readonly BrandFactClaimCandidate[],
): CanonicalClaimCandidate[] => {
  const candidates = expandCanonicalCandidatesUnfiltered(facts);
  const hasResearchDerivedHookTensions = candidates.some(
    (candidate) =>
      candidate.fieldPath === "brandVoiceCommunicationStyle.audienceTensions" &&
      candidate.sourceFieldPath === "targetAudience.humanLenses",
  );
  return hasResearchDerivedHookTensions
    ? candidates.filter(
        (candidate) =>
          candidate.fieldPath !== "brandVoiceCommunicationStyle.audienceTensions" ||
          candidate.sourceFieldPath === "targetAudience.humanLenses",
      )
    : candidates;
};

const toIsoDate = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
};

const toV1Provenance = (
  canonicalFieldPath: string,
  fact: BrandFactClaimCandidate,
): ResolvedBrandFactProvenanceV1 => ({
  canonicalFieldPath,
  sourceFactId: fact.id ?? null,
  sourceFieldPath: fact.fieldPath,
  strategyKey: fact.strategyKey ?? null,
  confidence: factConfidence(fact) >= 0 ? factConfidence(fact) : null,
  evidenceQuote: fact.evidenceQuote ?? null,
  rationale: fact.rationale ?? null,
  sourceUrl: fact.sourceUrl ?? null,
  createdAt: toIsoDate(fact.createdAt),
});

export const resolveBrandContextV1 = (
  facts: readonly BrandFactClaimCandidate[],
): { context: ResolvedBrandContextV1; provenance: ResolvedBrandContextProvenanceV1 } => {
  const selected = selectCanonicalBrandFactClaims(facts);
  const canonicalFields: Record<string, unknown | null> = {};
  const provenance: ResolvedBrandFactProvenanceV1[] = [];

  for (const [fieldPath, fact] of selected) {
    if (fieldPath === LEGACY_SOCIAL_EXPRESSION_PATH) continue;
    canonicalFields[fieldPath] = fact.valueJson ?? null;
    provenance.push(toV1Provenance(fieldPath, fact));
  }

  const legacyFact = selected.get(LEGACY_SOCIAL_EXPRESSION_PATH);
  const legacy = brandVoiceSocialExpressionSchema.safeParse(legacyFact?.valueJson);
  if (legacy.success && legacyFact) {
    for (const [legacyKey, canonicalPath] of Object.entries(
      LEGACY_SOCIAL_EXPRESSION_CANONICAL_FIELDS,
    )) {
      if (canonicalPath in canonicalFields) continue;
      canonicalFields[canonicalPath] = Reflect.get(legacy.data, legacyKey) ?? null;
      provenance.push(toV1Provenance(canonicalPath, legacyFact));
    }
  }

  provenance.sort((left, right) => left.canonicalFieldPath.localeCompare(right.canonicalFieldPath));
  return {
    context: resolvedBrandContextV1Schema.parse({ version: 1, canonicalFields }),
    provenance: resolvedBrandContextProvenanceV1Schema.parse({ version: 1, facts: provenance }),
  };
};

const emptyResolvedBrandContextV2 = (): ResolvedBrandContextV2 =>
  resolvedBrandContextV2Schema.parse({
    version: 2,
    coreIdentity: {},
    brandVoiceCommunicationStyle: {},
    positioningMarketContext: {},
    targetAudience: {},
    visualIdentitySnapshot: {},
    brandVisualUniverse: {},
  });

const normalizeTextList = (value: readonly string[] | null): string[] | null => {
  if (!value) return null;
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const item of value) {
    const trimmed = item.trim();
    const key = trimmed.toLocaleLowerCase();
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    normalized.push(trimmed);
  }
  return normalized;
};

const normalizeOptionalTextList = (value: readonly string[] | null): string[] | null => {
  const normalized = normalizeTextList(value);
  return normalized && normalized.length > 0 ? normalized : null;
};

const normalizeCompetitorKeys = ([name, url]: readonly [string, string]): string[] => {
  const nameKey = `name:${name.trim().toLocaleLowerCase()}`;
  try {
    return [`url:${new URL(url).hostname.toLocaleLowerCase().replace(/^www\./u, "")}`, nameKey];
  } catch {
    return [nameKey];
  }
};

const normalizeCompetitorBuckets = (context: ResolvedBrandContextV2): void => {
  const seen = new Set<string>();
  const normalizeBucket = (
    values: PositioningMarketContext["directCompetitors"] | null,
  ): PositioningMarketContext["directCompetitors"] | null => {
    if (!values) return null;
    const normalized: PositioningMarketContext["directCompetitors"] = [];
    for (const [rawName, rawUrl] of values) {
      const name = rawName.trim();
      const url = rawUrl.trim();
      const keys = normalizeCompetitorKeys([name, url]);
      if (!name || !url || keys.some((key) => seen.has(key))) continue;
      for (const key of keys) seen.add(key);
      normalized.push([name, url]);
    }
    return normalized;
  };

  context.positioningMarketContext.directCompetitors = normalizeBucket(
    context.positioningMarketContext.directCompetitors,
  );
  context.positioningMarketContext.indirectCompetitors = normalizeBucket(
    context.positioningMarketContext.indirectCompetitors,
  );
  context.positioningMarketContext.substituteSolutions = normalizeBucket(
    context.positioningMarketContext.substituteSolutions,
  );
};

const normalizeMechanics = (context: ResolvedBrandContextV2): void => {
  const preferred = normalizeTextList(context.brandVoiceCommunicationStyle.socialMechanics) as
    | BrandVoiceCommunicationStyle["socialMechanics"]
    | null;
  const avoided = normalizeTextList(context.brandVoiceCommunicationStyle.socialAvoidedMechanics) as
    | BrandVoiceCommunicationStyle["socialAvoidedMechanics"]
    | null;
  const overlaps = new Set((preferred ?? []).filter((mechanic) => avoided?.includes(mechanic)));
  context.brandVoiceCommunicationStyle.socialMechanics =
    preferred?.filter((mechanic) => !overlaps.has(mechanic)) ?? null;
  context.brandVoiceCommunicationStyle.socialAvoidedMechanics =
    avoided?.filter((mechanic) => !overlaps.has(mechanic)) ?? null;
};

const normalizePainPointMap = (context: ResolvedBrandContextV2): void => {
  context.targetAudience.painPoints = normalizeOptionalTextList(context.targetAudience.painPoints);
  const painPointKeys = new Set(
    (context.targetAudience.painPoints ?? []).map((painPoint) => painPoint.toLocaleLowerCase()),
  );
  if (!context.targetAudience.painPointMap) return;
  const seen = new Set<string>();
  const normalized = context.targetAudience.painPointMap.filter((item) => {
    const key = item.painPoint.trim().toLocaleLowerCase();
    if (!painPointKeys.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  context.targetAudience.painPointMap = normalized.length > 0 ? normalized : null;
};

const normalizeVisualColorRoles = (context: ResolvedBrandContextV2): void => {
  const palette = normalizeTextList([
    ...(context.visualIdentitySnapshot.colorPalette ?? []),
    ...(context.visualIdentitySnapshot.secondaryColorPalette ?? []),
  ]);
  const paletteByKey = new Map(
    (palette ?? []).map((color) => [color.toLocaleLowerCase(), color] as const),
  );
  if (!context.brandVisualUniverse.colorRoles) return;
  context.brandVisualUniverse.colorRoles = context.brandVisualUniverse.colorRoles.map((role) => ({
    ...role,
    colors:
      normalizeTextList(role.colors)?.flatMap((color) => {
        const canonical = paletteByKey.get(color.toLocaleLowerCase());
        return canonical ? [canonical] : [];
      }) ?? [],
  }));
};

const normalizeResolvedTextLists = (context: ResolvedBrandContextV2): void => {
  context.targetAudience.primaryAudienceSegments = normalizeOptionalTextList(
    context.targetAudience.primaryAudienceSegments,
  );
  context.targetAudience.secondaryAudienceSegments = normalizeTextList(
    context.targetAudience.secondaryAudienceSegments,
  );
  context.brandVoiceCommunicationStyle.restrictedTopics = normalizeTextList(
    context.brandVoiceCommunicationStyle.restrictedTopics,
  );
  context.brandVoiceCommunicationStyle.wordsToAvoid = normalizeTextList(
    context.brandVoiceCommunicationStyle.wordsToAvoid,
  );
  context.brandVoiceCommunicationStyle.wordsToEmphasize = normalizeTextList(
    context.brandVoiceCommunicationStyle.wordsToEmphasize,
  );
  context.brandVoiceCommunicationStyle.contentAngles = normalizeOptionalTextList(
    context.brandVoiceCommunicationStyle.contentAngles,
  );
  context.brandVoiceCommunicationStyle.audienceTensions = normalizeOptionalTextList(
    context.brandVoiceCommunicationStyle.audienceTensions,
  );
  context.visualIdentitySnapshot.colorPalette = normalizeOptionalTextList(
    context.visualIdentitySnapshot.colorPalette,
  );
  context.visualIdentitySnapshot.secondaryColorPalette = normalizeOptionalTextList(
    context.visualIdentitySnapshot.secondaryColorPalette,
  );
};

export const normalizeResolvedBrandContextV2 = (
  context: ResolvedBrandContextV2,
): ResolvedBrandContextV2 => {
  const normalized = structuredClone(context);
  normalizeResolvedTextLists(normalized);
  normalizeMechanics(normalized);
  normalizeCompetitorBuckets(normalized);
  normalizePainPointMap(normalized);
  normalizeVisualColorRoles(normalized);
  return resolvedBrandContextV2Schema.parse(normalized);
};

const setCanonicalContextValue = (
  context: ResolvedBrandContextV2,
  fieldPath: string,
  value: unknown,
): void => {
  const separator = fieldPath.indexOf(".");
  if (separator < 1) return;
  const section = fieldPath.slice(0, separator) as keyof Omit<ResolvedBrandContextV2, "version">;
  const field = fieldPath.slice(separator + 1);
  const target = context[section] as Record<string, unknown>;
  target[field] = value ?? null;
};

const claimIdFor = (fact: CanonicalClaimCandidate): string =>
  fact.id ?? `missing:${fact.sourceFieldPath}`;

export const resolveBrandContextV2 = (
  facts: readonly BrandFactClaimCandidate[],
): { context: ResolvedBrandContextV2; provenance: ResolvedBrandContextProvenanceV2 } => {
  const selected = selectCanonicalBrandFactClaims(expandCanonicalCandidates(facts));
  const context = emptyResolvedBrandContextV2();
  const selectedClaimIds: Record<string, string> = {};
  const claimsById: Record<string, ResolvedBrandClaimProvenanceV2> = {};

  for (const [canonicalPath, selectedFact] of selected) {
    const fact = selectedFact as CanonicalClaimCandidate;
    setCanonicalContextValue(context, canonicalPath, fact.valueJson);
    const claimId = claimIdFor(fact);
    selectedClaimIds[canonicalPath] = claimId;
    claimsById[claimId] ??= {
      sourceFieldPath: fact.sourceFieldPath,
      strategyKey: fact.strategyKey ?? null,
      confidence: factConfidence(fact) >= 0 ? factConfidence(fact) : null,
      evidenceQuote: fact.evidenceQuote ?? null,
      rationale: fact.rationale ?? null,
      sourceUrl: fact.sourceUrl ?? null,
      traceId: fact.traceId ?? null,
      createdAt: toIsoDate(fact.createdAt),
    };
  }

  return {
    context: normalizeResolvedBrandContextV2(context),
    provenance: resolvedBrandContextProvenanceV2Schema.parse({
      version: 2,
      selectedClaimIds,
      claimsById,
    }),
  };
};

export const inspectBrandContextV2Resolution = (
  facts: readonly BrandFactClaimCandidate[],
): BrandContextResolutionIssue[] => {
  const resolution = resolveBrandContextV2(facts);
  const issues: BrandContextResolutionIssue[] = [];
  const exactSelected = selectCanonicalBrandFactClaims(facts);
  for (const [fieldPath, fact] of exactSelected) {
    if (
      (typeof fact.valueJson === "string" && fact.valueJson.trim() === "") ||
      fact.valueJson === null
    ) {
      issues.push({
        code: "empty_value",
        fieldPath,
        sourceFieldPath: fieldPath,
        before: fact.valueJson,
        after: null,
      });
    }
    if (fieldPath === LEGACY_SOCIAL_EXPRESSION_PATH) {
      issues.push({
        code: "legacy_field",
        fieldPath,
        sourceFieldPath: fieldPath,
        before: fact.valueJson,
        after: null,
      });
    }
  }

  const selected = selectCanonicalBrandFactClaims(expandCanonicalCandidates(facts));
  for (const [fieldPath, fact] of selected) {
    const after = getResolvedBrandContextField(resolution.context, fieldPath);
    if (JSON.stringify(fact.valueJson) === JSON.stringify(after)) continue;
    issues.push({
      code: "normalized_value",
      fieldPath,
      sourceFieldPath: (fact as CanonicalClaimCandidate).sourceFieldPath,
      before: fact.valueJson,
      after,
    });
  }

  return issues.toSorted(
    (left, right) =>
      left.fieldPath.localeCompare(right.fieldPath) || left.code.localeCompare(right.code),
  );
};

export const convertResolvedBrandContextV1ToV2 = (input: {
  context: ResolvedBrandContextV1;
  provenance: ResolvedBrandContextProvenanceV1;
}): { context: ResolvedBrandContextV2; provenance: ResolvedBrandContextProvenanceV2 } => {
  const provenanceByPath = new Map(
    input.provenance.facts.map((fact) => [fact.canonicalFieldPath, fact]),
  );
  return resolveBrandContextV2(
    Object.entries(input.context.canonicalFields).map(([fieldPath, valueJson]) => {
      const provenance = provenanceByPath.get(fieldPath);
      return {
        id: provenance?.sourceFactId ?? undefined,
        fieldPath,
        valueJson,
        strategyKey: provenance?.strategyKey,
        confidence: provenance?.confidence,
        evidenceQuote: provenance?.evidenceQuote,
        rationale: provenance?.rationale,
        sourceUrl: provenance?.sourceUrl,
        createdAt: provenance?.createdAt,
      };
    }),
  );
};

export const toResolvedBrandContextSnapshotV2 = (
  snapshot: ResolvedBrandContextSnapshot,
): ResolvedBrandContextSnapshotV2 => {
  if (snapshot.schemaVersion === 2) return snapshot;
  const converted = convertResolvedBrandContextV1ToV2(snapshot);
  return { ...snapshot, schemaVersion: 2, ...converted };
};

const resolvedContextEntries = (context: ResolvedBrandContext): Array<[string, unknown]> => {
  if (context.version === 1) return Object.entries(context.canonicalFields);
  return Object.entries(context).flatMap(([section, fields]) =>
    section === "version"
      ? []
      : Object.entries(fields as Record<string, unknown>).flatMap(([field, value]) =>
          value === null ? [] : [[`${section}.${field}`, value] as [string, unknown]],
        ),
  );
};

export const buildResolvedBrandProfilePromptContext = (
  context: ResolvedBrandContext,
  options: { excludedFieldPaths?: Iterable<string> } = {},
) =>
  buildBrandProfilePromptContext(
    resolvedContextEntries(context).map(([fieldPath, valueJson]) => ({ fieldPath, valueJson })),
    options,
  );

export const buildResolvedBrandProfilePromptContextForTask = (
  context: ResolvedBrandContext,
  task: ResolvedBrandContextTask,
  options: { excludedFieldPaths?: Iterable<string> } = {},
) => {
  const allowed = new Set<string>(RESOLVED_BRAND_CONTEXT_TASK_FIELD_PATHS[task]);
  const excluded = new Set(options.excludedFieldPaths ?? []);
  const entries = resolvedContextEntries(context);
  const projectValue = (fieldPath: string, value: unknown): unknown => {
    if (!Array.isArray(value)) return value;
    if (task === "copy" && fieldPath === "targetAudience.humanLenses") return value.slice(0, 4);
    if (task === "copy" && fieldPath === "targetAudience.domainTerritories") {
      return value.slice(0, 6);
    }
    if (task === "image" && fieldPath.startsWith("targetAudience.")) return value.slice(0, 4);
    return value;
  };

  const facts = entries.flatMap(([fieldPath, valueJson]) =>
    allowed.has(fieldPath) &&
    !excluded.has(fieldPath) &&
    fieldPath !== BRAND_VISUAL_LOOK_FIELD_PATH &&
    !fieldPath.startsWith(BRAND_VISUAL_UNIVERSE_FIELD_PATH_PREFIX)
      ? [{ fieldPath, valueJson: projectValue(fieldPath, valueJson) }]
      : [],
  );

  if (task === "image" && !excluded.has(BRAND_VISUAL_LOOK_FIELD_PATH)) {
    const existingLook = entries.find(
      ([fieldPath]) => fieldPath === BRAND_VISUAL_LOOK_FIELD_PATH && !excluded.has(fieldPath),
    )?.[1];
    const visualUniverseSource = Object.fromEntries(
      entries.flatMap(([fieldPath, valueJson]) =>
        allowed.has(fieldPath) &&
        !excluded.has(fieldPath) &&
        fieldPath.startsWith(BRAND_VISUAL_UNIVERSE_FIELD_PATH_PREFIX)
          ? [[fieldPath.slice(BRAND_VISUAL_UNIVERSE_FIELD_PATH_PREFIX.length), valueJson]]
          : [],
      ),
    );
    facts.push({
      fieldPath: BRAND_VISUAL_LOOK_FIELD_PATH,
      valueJson: projectBrandVisualUniverseToLook(existingLook ?? visualUniverseSource),
    });
  }

  return buildBrandProfilePromptContext(facts);
};

export const getResolvedBrandContextField = (
  context: ResolvedBrandContext,
  fieldPath: string,
): unknown | null => {
  if (context.version === 1) return context.canonicalFields[fieldPath] ?? null;
  const separator = fieldPath.indexOf(".");
  if (separator < 1) return null;
  const section = context[
    fieldPath.slice(0, separator) as keyof Omit<ResolvedBrandContextV2, "version">
  ] as Record<string, unknown> | undefined;
  return section?.[fieldPath.slice(separator + 1)] ?? null;
};
